import { useState, useEffect } from 'react';
import AsyncSelect from 'react-select/async';
import COLORS from '@shared/colorConstants';
import CustomIcon, { ValidIconTypes } from '@components/shared/CustomIcon';
import useAnalytics from '@shared/useAnalytics';
import { useHistory } from 'react-router-dom';
import api from '@shared/api';
import routes from '@root/routes';
import { GlobalSearchResponse, GlobalSearchBuilding } from '@root/types';
import { components } from 'react-select';
import { useTranslation } from 'react-i18next';
import useHandleClickOutside from '@shared/useHandleClickOutside';
import s from './Navigation.module.less';

type Props = {
  searchOpen: boolean;
  setSearchOpen: (open: boolean) => void;
};

type OptionType = {
  label: string;
  value: string;
  group: 'building' | 'location';
  labelAsideText: string;
};
type GroupType = {
  label: string;
  icon: ValidIconTypes;
  options: OptionType[];
};

const Control = props => {
  return <components.Control {...props} className={s.selectContainer} />;
};

const formatGroupLabel = ({ label, icon }: GroupType) => (
  <div className={s.groupLabel}>
    <CustomIcon type={icon} className={s.groupLabelIcon} />
    {label}
  </div>
);

const Option = props => {
  /* eslint-disable react/no-danger */
  /* eslint-disable react/destructuring-assignment */
  return (
    <components.Option {...props} className={s.buildingOption}>
      <div dangerouslySetInnerHTML={{ __html: props.data.label }} />
      <div>{props.data.labelAsideText}</div>
    </components.Option>
  );
};
/* eslint-enable react/no-danger */
/* eslint-enable react/destructuring-assignment */

const Menu = props => <components.Menu {...props} className={s.menu} />;

const customComponents = {
  Control,
  Menu,
  Option,
  DropdownIndicator: null,
  IndicatorSeparator: null,
};

export default function BuildingSearch({ searchOpen, setSearchOpen }: Props) {
  const { t } = useTranslation('navigation');
  const history = useHistory();

  const customStyles = {
    container: base => ({
      ...base,
      width: '100%',
    }),
    control: base => ({
      ...base,
      border: 0,
    }),
    option: (base, state) => ({
      ...base,
      background: state.isFocused ? COLORS.egg50 : 'white',
    }),
    groupHeading: base => ({
      ...base,
      marginBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
    }),
    group: base => ({
      ...base,
      paddingTop: 0,
      paddingBottom: 0,
    }),
    menuList: base => ({
      ...base,
      paddingTop: 0,
      paddingBottom: 0,
    }),
    menu: base => ({ ...base, marginTop: 0 }),
    placeholder: base => ({
      ...base,
      color: COLORS.black35,
    }),
  };

  const { clickToPage, PARAMETERS } = useAnalytics();
  const [buildingOptions, setBuildingOptions] = useState<GlobalSearchBuilding[]>([]);
  const [searchValue, setSearchValue] = useState<string | null>(null);

  const handleChange = ({ value: slug }: OptionType, { action }) => {
    if (action === 'select-option') {
      const building = buildingOptions.find(b => b.value === slug);
      clickToPage(
        {
          sourceContent: PARAMETERS.searchBar,
          destination: PARAMETERS.buildingPage,
          actionType: 'CLICK_ON_BUILDING_IN_SEARCH',
        },
        {
          searchValue,
          destinationBuilding: {
            ...(building?.meta.analytics || {}),
            buildingPreview: false,
          },
        },
      );
      history.push(routes.building(slug));
    }
  };

  const loadOptions = async (inputValue: string): Promise<GroupType[]> => {
    const response = await api.fetch(routes.api.globalSearches(inputValue));
    const rawSearchResults: GlobalSearchResponse = await response.json();
    setBuildingOptions(rawSearchResults.buildings);
    setSearchValue(inputValue);

    return [
      {
        label: t('homepage:search.buildingsGroupLabel'),
        icon: 'buildings-multiple',
        options: rawSearchResults.buildings.map(
          (b): OptionType => ({
            label: b.highlightedLabel,
            value: b.value,
            group: 'building',
            labelAsideText: b.labelAsideText,
          }),
        ),
      },
    ];
  };

  const ref = useHandleClickOutside<HTMLLIElement>(() => setSearchOpen(false));
  const handleKeyDown = event => {
    if (event.key === 'Escape') {
      setSearchOpen(false);
    }
  };
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  });

  /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
  return (
    <li ref={ref}>
      <button type="button" className={s.navigationLink} onClick={() => setSearchOpen(true)}>
        <div className={s.buildingSearchNavigationLink}>
          <CustomIcon className={s.icon} type="buildings-multiple"></CustomIcon>
          <span className={s.navText}>{t('building')}</span>
        </div>
        {searchOpen ? (
          <div className={s.buildingSearchContainer}>
            {/* @ts-expect-error */}
            <AsyncSelect
              autoFocus
              className={s.search}
              name="building-search"
              components={customComponents}
              styles={customStyles}
              placeholder={t('typeBuildingName')}
              loadOptions={loadOptions}
              onChange={handleChange}
              formatGroupLabel={formatGroupLabel}
              aria-label={t('buildingSearch')}
              noOptionsMessage={() => t('homepage:search.noResultsMessage')}
            />
          </div>
        ) : null}
      </button>
    </li>
  );
}
/* eslint-enable jsx-a11y/no-noninteractive-element-interactions */
